import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  Shield, CheckCircle, XCircle, Clock, ArrowLeft, RefreshCw,
  User, Mail, Calendar, FileText, Search, Filter
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AccreditationManagement = () => {
  const { token } = useAuth();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedApp, setSelectedApp] = useState(null);
  const [stats, setStats] = useState({ pending: 0, approved: 0, rejected: 0 });

  useEffect(() => {
    fetchApplications();
  }, [statusFilter]);

  const fetchApplications = async () => {
    setLoading(true);
    try {
      const url = statusFilter === 'all' 
        ? `${API}/accreditation/applications`
        : `${API}/accreditation/applications?status_filter=${statusFilter}`;
      
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setApplications(data.applications || []);
        setStats(data.by_status || { pending: 0, approved: 0, rejected: 0 });
      }
    } catch (error) {
      console.error('Error fetching applications:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (userId) => {
    try {
      const response = await fetch(`${API}/accreditation/approve/${userId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ notes: 'Approved by admin' })
      });
      
      if (response.ok) {
        toast.success('Accreditation approved');
        fetchApplications();
        setSelectedApp(null);
      } else {
        toast.error('Failed to approve');
      }
    } catch (error) {
      toast.error('Error approving application');
    }
  };

  const handleReject = async (userId) => {
    const reason = prompt('Enter rejection reason:');
    if (!reason) return;
    
    try {
      const response = await fetch(`${API}/accreditation/reject/${userId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ reason })
      });
      
      if (response.ok) {
        toast.success('Accreditation rejected');
        fetchApplications();
        setSelectedApp(null);
      } else {
        toast.error('Failed to reject');
      }
    } catch (error) {
      toast.error('Error rejecting application');
    }
  };

  const filteredApps = applications.filter(app => 
    app.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    app.user_id?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return <span className="px-2 py-1 rounded text-xs bg-green-900/50 text-green-400">Approved</span>;
      case 'rejected':
        return <span className="px-2 py-1 rounded text-xs bg-red-900/50 text-red-400">Rejected</span>;
      default:
        return <span className="px-2 py-1 rounded text-xs bg-yellow-900/50 text-yellow-400">Pending</span>;
    }
  };

  return (
    <div className="min-h-screen bg-[#0a1628]" data-testid="accreditation-management">
      {/* Header */}
      <div className="bg-[#0d1d35] border-b border-[#1a2d4a] px-6 py-4">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Link to="/admin-dashboard">
              <Button variant="ghost" size="sm" className="text-gray-400 hover:text-white">
                <ArrowLeft className="w-4 h-4 mr-1" />
                Dashboard
              </Button>
            </Link>
            <div>
              <h1 className="text-2xl font-bold text-white flex items-center gap-2">
                <Shield className="w-6 h-6 text-[#b8860b]" />
                Accreditation Management
              </h1>
              <p className="text-gray-400 text-sm">Review and manage investor accreditation applications</p>
            </div>
          </div>
          <Button variant="outline" onClick={fetchApplications} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <Card className="bg-[#0d1d35] border-[#1a2d4a]">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3 mb-2">
                <FileText className="w-5 h-5 text-gray-400" />
                <p className="text-gray-400 text-sm">Total Applications</p>
              </div>
              <p className="text-2xl font-bold text-white">{applications.length}</p>
            </CardContent>
          </Card>
          <Card className="bg-[#0d1d35] border-[#1a2d4a] cursor-pointer" onClick={() => setStatusFilter('pending')}>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3 mb-2">
                <Clock className="w-5 h-5 text-yellow-400" />
                <p className="text-gray-400 text-sm">Pending Review</p>
              </div>
              <p className="text-2xl font-bold text-yellow-400">{stats.pending}</p>
            </CardContent>
          </Card>
          <Card className="bg-[#0d1d35] border-[#1a2d4a] cursor-pointer" onClick={() => setStatusFilter('approved')}>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3 mb-2">
                <CheckCircle className="w-5 h-5 text-green-400" />
                <p className="text-gray-400 text-sm">Approved</p>
              </div>
              <p className="text-2xl font-bold text-green-400">{stats.approved}</p>
            </CardContent>
          </Card>
          <Card className="bg-[#0d1d35] border-[#1a2d4a] cursor-pointer" onClick={() => setStatusFilter('rejected')}>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3 mb-2">
                <XCircle className="w-5 h-5 text-red-400" />
                <p className="text-gray-400 text-sm">Rejected</p>
              </div>
              <p className="text-2xl font-bold text-red-400">{stats.rejected}</p>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <div className="flex gap-4 mb-6">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <Input 
              placeholder="Search by name or user ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-[#0d1d35] border-[#1a2d4a] text-white"
            />
          </div>
          <select 
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 bg-[#0d1d35] border border-[#1a2d4a] text-white rounded-md"
          >
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Applications List */}
          <div className="lg:col-span-2">
            <Card className="bg-[#0d1d35] border-[#1a2d4a]">
              <CardHeader>
                <CardTitle className="text-white">Applications ({filteredApps.length})</CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="text-center py-8 text-gray-400">Loading...</div>
                ) : filteredApps.length === 0 ? (
                  <div className="text-center py-8 text-gray-400">No applications found</div>
                ) : (
                  <div className="space-y-3 max-h-[500px] overflow-y-auto">
                    {filteredApps.map((app, idx) => (
                      <div 
                        key={idx}
                        onClick={() => setSelectedApp(app)}
                        className={`p-4 rounded-lg cursor-pointer transition-colors ${
                          selectedApp?.user_id === app.user_id
                            ? 'bg-[#b8860b]/20 border border-[#b8860b]/50'
                            : 'bg-[#1a2d4a]/50 hover:bg-[#1a2d4a]'
                        }`}
                      >
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="text-white font-medium">{app.full_name || 'Unknown'}</p>
                            <p className="text-sm text-gray-400">{app.verification_method?.replace('_', ' ')}</p>
                          </div>
                          <div className="text-right">
                            {getStatusBadge(app.status)}
                            <p className="text-xs text-gray-500 mt-1">
                              {new Date(app.created_at).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Application Details */}
          <div>
            <Card className="bg-[#0d1d35] border-[#1a2d4a]">
              <CardHeader>
                <CardTitle className="text-white">Application Details</CardTitle>
              </CardHeader>
              <CardContent>
                {selectedApp ? (
                  <div className="space-y-4">
                    <div>
                      <p className="text-xs text-gray-500">Full Name</p>
                      <p className="text-white">{selectedApp.full_name}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">User ID</p>
                      <p className="text-white text-sm font-mono">{selectedApp.user_id}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Verification Method</p>
                      <p className="text-white capitalize">{selectedApp.verification_method?.replace('_', ' ')}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Status</p>
                      {getStatusBadge(selectedApp.status)}
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Application Date</p>
                      <p className="text-white text-sm">{new Date(selectedApp.created_at).toLocaleString()}</p>
                    </div>
                    
                    {selectedApp.status === 'pending' && (
                      <div className="flex gap-3 pt-4">
                        <Button 
                          className="flex-1 bg-green-600 hover:bg-green-700"
                          onClick={() => handleApprove(selectedApp.user_id)}
                        >
                          <CheckCircle className="w-4 h-4 mr-2" />
                          Approve
                        </Button>
                        <Button 
                          variant="outline"
                          className="flex-1 border-red-500 text-red-400 hover:bg-red-900/20"
                          onClick={() => handleReject(selectedApp.user_id)}
                        >
                          <XCircle className="w-4 h-4 mr-2" />
                          Reject
                        </Button>
                      </div>
                    )}
                    
                    {selectedApp.status === 'approved' && (
                      <div className="p-3 bg-green-900/20 border border-green-500/30 rounded-lg">
                        <p className="text-green-400 text-sm">
                          Approved on {new Date(selectedApp.verified_at).toLocaleDateString()}
                        </p>
                        {selectedApp.approved_by && (
                          <p className="text-gray-400 text-xs mt-1">By: {selectedApp.approved_by}</p>
                        )}
                      </div>
                    )}
                    
                    {selectedApp.status === 'rejected' && (
                      <div className="p-3 bg-red-900/20 border border-red-500/30 rounded-lg">
                        <p className="text-red-400 text-sm">
                          Rejected on {new Date(selectedApp.rejected_at).toLocaleDateString()}
                        </p>
                        {selectedApp.rejection_reason && (
                          <p className="text-gray-400 text-xs mt-1">Reason: {selectedApp.rejection_reason}</p>
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <User className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    Select an application to view details
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccreditationManagement;
